import { useEffect, useState } from "react";
import {
  getVolunteerOpportunities,
  createVolunteerOpportunity,
  updateVolunteerOpportunity,
  deleteVolunteerOpportunity,
} from "../../../api/apiServices";

const iconOptions = [
  { value: "ri-heart-pulse-line", label: "Heart Pulse" },
  { value: "ri-megaphone-line", label: "Megaphone" },
  { value: "ri-hand-heart-line", label: "Hand Heart" },
  { value: "ri-lightbulb-line", label: "Lightbulb" },
  { value: "ri-user-heart-line", label: "User Heart" },
  { value: "ri-team-line", label: "Team" },
  { value: "ri-shield-check-line", label: "Shield Check" },
  { value: "ri-book-open-line", label: "Book Open" },
  { value: "ri-home-heart-line", label: "Home Heart" },
  { value: "ri-mental-health-line", label: "Mental Health" },
];

export default function VolunteerOpportunitiesSection() {
  const [opportunities, setOpportunities] = useState([]);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const emptyOpportunity = {
    icon: "ri-heart-pulse-line",
    title: "",
    description: "",
    isActive: true,
  };

  /* ---------------- FETCH ---------------- */
  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const res = await getVolunteerOpportunities();
      setOpportunities(res.data || []);
    } catch (err) {
      console.error("Failed to fetch opportunities", err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- SAVE (FORM DATA) ---------------- */
  const handleSave = async () => {
  try {
    const payload = {
      icon: editingOpportunity.icon,
      title: editingOpportunity.title,
      description: editingOpportunity.description,
      isActive: editingOpportunity.isActive,
    };

    if (isAdding) {
      await createVolunteerOpportunity(payload);
    } else {
      await updateVolunteerOpportunity(editingOpportunity._id, payload);
    }

    await fetchOpportunities();
    setEditingOpportunity(null);
    setIsAdding(false);

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  } catch (err) {
    console.error("Save failed", err);
  }
};


  /* ---------------- DELETE ---------------- */
  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this opportunity?")) return;

    try {
      await deleteVolunteerOpportunity(id);
      setOpportunities((prev) => prev.filter((o) => o._id !== id));
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  /* ---------------- TOGGLE ACTIVE (FORM DATA) ---------------- */
  const toggleActive = async (opportunity) => {
    try {
      await updateVolunteerOpportunity(opportunity._id, {
        isActive: !opportunity.isActive,
      });

      setOpportunities((prev) =>
        prev.map((o) =>
          o._id === opportunity._id
            ? { ...o, isActive: !o.isActive }
            : o
        )
      );
    } catch (err) {
      console.error("Toggle failed", err);
    }
  };

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Volunteer Opportunities
          </h2>
          <p className="text-gray-600 text-sm">
            Manage volunteer positions
          </p>
        </div>

        <button
          onClick={() => {
            setEditingOpportunity(emptyOpportunity);
            setIsAdding(true);
          }}
          className="bg-[#6a4bc3] text-white px-6 py-2.5 rounded-lg flex items-center gap-2"
        >
          <i className="ri-add-line" />
          Add Opportunity
        </button>
      </div>

      {showSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
          Opportunity saved successfully
        </div>
      )}

      {/* GRID */}
      {!editingOpportunity && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            opportunities.map((opportunity) => (
              <div
                key={opportunity._id}
                className={`bg-white rounded-xl p-6 border ${
                  !opportunity.isActive ? "opacity-60" : ""
                }`}
              >
                <div className="flex justify-between mb-4">
                  <div className="w-14 h-14 bg-[#6a4bc3]/10 rounded-xl flex items-center justify-center">
                    <i
                      className={`${opportunity.icon} text-2xl text-[#c9a24d]`}
                    />
                  </div>

                  <button
                    onClick={() => toggleActive(opportunity)}
                    className={`px-3 py-1 rounded-full text-xs ${
                      opportunity.isActive
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {opportunity.isActive ? "Active" : "Inactive"}
                  </button>
                </div>

                <h3 className="font-semibold mb-2">
                  {opportunity.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {opportunity.description}
                </p>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingOpportunity(opportunity);
                      setIsAdding(false);
                    }}
                    className="flex-1 py-2 text-sm bg-[#6a4bc3]/10 rounded-lg"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(opportunity._id)}
                    className="w-10 h-10 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* FORM */}
      {editingOpportunity && (
        <div className="bg-white rounded-xl p-6 border space-y-6">
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
            {iconOptions.map((icon) => (
              <button
                key={icon.value}
                onClick={() =>
                  setEditingOpportunity({
                    ...editingOpportunity,
                    icon: icon.value,
                  })
                }
                className={`w-12 h-12 rounded-lg ${
                  editingOpportunity.icon === icon.value
                    ? "bg-[#6a4bc3] text-white"
                    : "bg-gray-100"
                }`}
              >
                <i className={`${icon.value} text-xl`} />
              </button>
            ))}
          </div>

          <input
            className="w-full border px-4 py-2 rounded-lg"
            placeholder="Title"
            value={editingOpportunity.title}
            onChange={(e) =>
              setEditingOpportunity({
                ...editingOpportunity,
                title: e.target.value,
              })
            }
          />

          <textarea
            rows={3}
            className="w-full border px-4 py-2 rounded-lg"
            placeholder="Description"
            value={editingOpportunity.description}
            onChange={(e) =>
              setEditingOpportunity({
                ...editingOpportunity,
                description: e.target.value,
              })
            }
          />

          <label className="flex gap-2 text-sm">
            <input
              type="checkbox"
              checked={editingOpportunity.isActive}
              onChange={(e) =>
                setEditingOpportunity({
                  ...editingOpportunity,
                  isActive: e.target.checked,
                })
              }
            />
            Active
          </label>

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              className="bg-[#6a4bc3] text-white px-6 py-2 rounded-lg"
            >
              {isAdding ? "Add Opportunity" : "Save Changes"}
            </button>
            <button
              onClick={() => {
                setEditingOpportunity(null);
                setIsAdding(false);
              }}
              className="border px-6 py-2 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
